/**
 * The speaker button beside the gear, in the lobby and in a match: a small
 * popover with mute and the music and effects levels, so nobody has to find
 * them in the settings panel mid-game. The icon doubles as the mute state.
 */

import { getAudio } from '../audio/audio.js';
import { button, el } from './dom.js';
import { soundControls } from './soundSettings.js';

const SPEAKER = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z"/>'
  + '<path d="M16 8.5a4.5 4.5 0 0 1 0 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>'
  + '<path d="M18.5 5.5a8 8 0 0 1 0 13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
const MUTED = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z"/>'
  + '<path d="M16 9l5 6M21 9l-5 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';

export interface SoundPopover {
  /** Redraw the icon after the mute state changed elsewhere (the M key). */
  refresh(): void;
  close(): void;
}

export function mountSoundPopover(ui: HTMLElement): SoundPopover {
  const audio = getAudio();
  const controls = soundControls();
  const panel = el('div', { class: 'sound-popover', role: 'group', 'aria-label': 'Sound levels', hidden: true }, ...controls.nodes);
  let open = false;
  const speaker = button('', () => setOpen(!open), 'btn speaker-btn');
  speaker.title = 'Music and effects levels';
  speaker.setAttribute('aria-expanded', 'false');

  function refresh(): void {
    speaker.innerHTML = audio.muted ? MUTED : SPEAKER;
    speaker.setAttribute('aria-label', audio.muted ? 'Sound levels (muted)' : 'Sound levels');
  }

  function setOpen(v: boolean): void {
    open = v;
    panel.hidden = !v;
    speaker.setAttribute('aria-expanded', String(v));
    if (v) controls.sync();
    refresh();
  }

  // A change made in the popover shows on the icon at once.
  panel.addEventListener('change', refresh);
  document.addEventListener('pointerdown', (ev) => {
    if (open && ev.target instanceof Node && !panel.contains(ev.target) && !speaker.contains(ev.target)) setOpen(false);
  });
  document.addEventListener('keydown', (ev) => {
    if (open && ev.key === 'Escape') setOpen(false);
  });
  refresh();
  ui.append(speaker, panel);
  return { refresh, close: () => setOpen(false) };
}
